import React, { ReactElement } from "react";
import "./index.css";
import reportWebVitals from "./reportWebVitals";

import { tx } from "@transifex/native";

import * as Sentry from "@sentry/react";
import { ThemeProvider } from "styled-components";
import { createMainTheme } from "p6-react-shared";
import Layout from "./layout/Layout";
import { AppGeneralContextProvider } from "./context/AppGeneralContext";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import "moment/locale/de";
import "./assets/css/selectSearch.css";

import "./assets/proximaNovaFont/proximaNova.css";
import "./assets/phase6-icon-font/phase6icons.css";
import "./assets/phase6-icon-font/p6-icon-font.css";

// Axios
import axios from "axios";
import { cp } from "./config";
import { initSentry } from "./sentry";
import { LAST_TX_LANG_STORAGE_KEY, normalizeTxLocale } from "./helpers/TxLocaleHelper";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { createRoot } from "react-dom/client";

const initTranslations = (): void => {
    tx.init({
        token: process.env.REACT_APP_TRANSIFEX_TOKEN,
        cdsHost: cp.cfg.REACT_APP_TRANSIFEX_CDS_HOST,
        filterTags: "p6u",
    });
    const storedLanguage = localStorage.getItem(LAST_TX_LANG_STORAGE_KEY);
    const lastLanguage = normalizeTxLocale(storedLanguage);
    if (storedLanguage && storedLanguage !== lastLanguage) {
        localStorage.setItem(LAST_TX_LANG_STORAGE_KEY, lastLanguage);
    }
    tx.setCurrentLocale(lastLanguage).catch((error) => {
        console.error("Failed to load translations", error);
        Sentry.captureException(error, { extra: { locale: lastLanguage } });
    });
};

axios
    .get(window.location.href.includes("localhost") ? "/config.json" : "config.json")
    .then((response) => {
        cp.cfg = response.data;
        initTranslations();
        initSentry();

        // Console helpers for producing legacy tests. The check is compiled away in production builds,
        // so neither this import nor the module reaches the bundle.
        if (process.env.NODE_ENV === "development") {
            import("./devtools/makeTestLegacy");
        }

        return (
            <React.StrictMode>
                <Sentry.ErrorBoundary>
                    <ThemeProvider theme={createMainTheme}>
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                            <AppGeneralContextProvider>
                                <Layout />
                            </AppGeneralContextProvider>
                        </LocalizationProvider>
                    </ThemeProvider>
                </Sentry.ErrorBoundary>
            </React.StrictMode>
        );
    })
    .catch(() => {
        return <p style={{ color: "red", textAlign: "center" }}>Error while fetching global config</p>;
    })
    .then((reactElement: ReactElement) => {
        const rootElement = document.getElementById("root");
        if (rootElement) {
            createRoot(rootElement).render(reactElement);
        }
    });

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
